import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { confirm, showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { extractObjectFromDataTable } from 'hooks/datatable';
import { useUserContext } from 'providers/UserProvider';
import API from 'services/API';
import { doesUserOwnItem, userHasRole } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import NoteForm from 'UI/components/organisms/NoteForm';
import { componentDimensions } from 'UI/constants/dimensions';
import { TabKeys } from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { SvgNoNotes } from 'UI/res/icons/milano';
import { getErrorMessage } from 'UI/utils/index';

import { extraNoteRenderers } from '../constants';
import ProfileTableLayout from '../ProfileTableLayout';

import { notesColumns } from './notesColumns';

const {
  inventoryProfiles: {
    sections: {
      tabs: {
        activityNote: { noteActionText }
      }
    }
  }
} = strings;
const { emptyState } = componentDimensions;

export const userCanManage = ({ rowData }, tabData, initialColumns, user) => {
  const { id } = extractObjectFromDataTable(initialColumns, ['id'], rowData);

  const selectedItem = tabData.find(item => item.id === id);
  const canManage = doesUserOwnItem(user, selectedItem) && !selectedItem.created_by_system;

  return canManage;
};

const NotesTab = ({
  DataTableProps,
  endpoint,
  tablePreferencesKey,
  isQuickViewMode = false,
  itemName = '',
  onActivityComplete,
  profileId,
  profileModule
}) => {
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState({
    isDrawerOpen: false,
    selectedItem: null,
    shouldRefreshTableData: false,
    id: null,
    entityId: null,
    entity: profileModule
  });

  const [user] = useUserContext();

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   A C T I O N S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const openDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isDrawerOpen: true,
      shouldRefreshTableData: false,
      entityId: profileId
    }));
  };

  const handleEditNoteClick = item => {
    setUiState(prev => ({
      ...prev,
      id: item.id,
      isDrawerOpen: true,
      selectedItem: item,
      entityId: profileId,
      shouldRefreshTableData: false
    }));
  };

  const closeDrawer = () => {
    setUiState(prev => ({
      ...prev,
      id: null,
      isDrawerOpen: false,
      entityId: null,
      entity: profileModule,
      selectedItem: null
    }));
  };

  const handleNoteCompleted = data => {
    setUiState(prev => ({
      ...prev,
      shouldRefreshTableData: true
    }));
    onActivityComplete && onActivityComplete(data);
    closeDrawer();
  };

  const handleDeleteClick = async (itemId, getData) =>
    dispatch(
      confirm({
        severity: 'warning',
        title: strings.inventoryProfiles.sections.tabs.note.title,
        message: strings.inventoryProfiles.sections.tabs.note.message,
        ...strings.shared.ui.keepDeleteButtonCopies,
        onConfirm: async ok => {
          if (!ok) return;

          try {
            const response = await API.delete(`${endpoint}/notes/${itemId}`);
            if (response.status === HTTPStatusCodes.Ok) {
              await getData();
              dispatch(
                showAlert({
                  severity: 'success',
                  title: strings.inventoryProfiles.sections.tabs.note.deletedMessage
                })
              );
            }
          } catch (error) {
            dispatch(
              showAlert({
                severity: 'error',
                title: 'Error',
                body: getErrorMessage(error)
              })
            );
          }
        }
      })
    );

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   C O L U M N S   C O N F I G
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const isOperationsUser = userHasRole(Roles.Operations, user);

  const notesActions = [
    { name: 'view', callback: handleEditNoteClick },
    {
      name: 'edit',
      callback: handleEditNoteClick,
      checkIfShouldBeEnabled: ({ rowData }, tabData, initialColumns) =>
        userCanManage({ rowData }, tabData, initialColumns, user)
    },
    {
      name: 'delete',
      callback: handleDeleteClick,
      pickId: true,
      isVisible: isOperationsUser
    }
  ];

  return (
    <>
      <ProfileTableLayout
        actionText={noteActionText}
        customEmptyState={<SvgNoNotes size={emptyState.width} />}
        customName="created_by_system"
        DataTableProps={DataTableProps}
        extraRenderers={extraNoteRenderers}
        finalActions={notesActions}
        hasProfileLoaded={profileId}
        initialColumns={notesColumns}
        isQuickViewMode={isQuickViewMode}
        onNewItemClick={openDrawer}
        orderByOptions={{ column: 'updated_at', direction: 'desc' }}
        profileModule={profileModule}
        shouldRefresh={uiState.shouldRefreshTableData}
        tabKey={TabKeys.Notes}
        tablePreferencesKey={tablePreferencesKey}
      />

      <When condition={uiState.isDrawerOpen}>
        <NoteForm
          endpoint={endpoint}
          FPDrawerProps={{ open: uiState.isDrawerOpen }}
          isQuickViewMode={isQuickViewMode}
          itemName={itemName}
          onNoteClosed={closeDrawer}
          onNoteCompleted={handleNoteCompleted}
          selectedItem={uiState.selectedItem}
        />
      </When>
    </>
  );
};

export default NotesTab;
